import { create } from "zustand";
import { persist } from "zustand/middleware";

import { detectBrowser, detectOS } from "@utils/detect";

import { LARUCHE_IDENTIFIER } from "@constants/index";

import { SignInAttempt } from "@customTypes/telemetry";


const SUBMIT_INTERVAL = import.meta.env.DEV
  ? 1000 * 60 * 60 * 6 // 6 hours
  : 1000 * 60 * 60 * 24; // 24 hours
const CHECK_INTERVAL = import.meta.env.DEV
  ? 1000 * 60 * 30 // 30 mins
  : 1000 * 60 * 30; // 30 mins

// Helper function to check if 24 hours have passed since the timestamp
function isTelemetryDue(timestamp: string): boolean {
  const currentTime = new Date().getTime();
  const telemetryTime = new Date(timestamp).getTime();
  const timeDifference = currentTime - telemetryTime;
  return timeDifference >= SUBMIT_INTERVAL;
}

export interface TelemetryStore {
  browser: string;
  setBrowser: (value: string) => void;

  idleSignOutCount: number;
  setIdleSignOutCount: (value: number | ((prev: number) => number)) => void;

  idleTime: number;
  setIdleTime: (value: number | ((prev: number) => number)) => void;

  idleWarningCount: number;
  setIdleWarningCount: (value: number | ((prev: number) => number)) => void;

  language: string;
  setLanguage: (value: string) => void;

  os: string;
  setOs: (value: string) => void;

  screenOrientation: string;
  setScreenOrientation: (value: string) => void;

  screenResolution: string;
  setScreenResolution: (value: string) => void;

  signInAttempts: SignInAttempt[];
  setSignInAttempts: (
    value: SignInAttempt[] | ((prev: SignInAttempt[]) => SignInAttempt[])
  ) => void;

  timestamp: string;
  setTimestamp: (value: string) => void;

  totalTimeSpent: number;
  setTotalTimeSpent: (value: number | ((prev: number) => number)) => void;

  userId: string;
  setUserId: (value: string) => void;

  // Methods
  checkAndSendTelemetry: (submitTelemetryFn: () => void) => void;
  startTelemetryInterval: (
    submitTelemetryFn: () => void
  ) => NodeJS.Timer | undefined;
  resetTelemetry: () => void;
}

const initialState: Pick<
  TelemetryStore,
  | "browser"
  | "idleSignOutCount"
  | "idleTime"
  | "idleWarningCount"
  | "language"
  | "os"
  | "screenOrientation"
  | "screenResolution"
  | "signInAttempts"
  | "timestamp"
  | "totalTimeSpent"
  | "userId"
> = {
  browser: detectBrowser() || "other",
  idleSignOutCount: 0,
  idleTime: 0,
  idleWarningCount: 0,
  language: "", // Can't use i18n.language here directly - doesn't show up in local storage
  os: detectOS() || "other", // Mantine's "use-os" hook covers this
  screenOrientation: "",
  screenResolution: "",
  signInAttempts: [],
  timestamp: new Date().toISOString(),
  totalTimeSpent: 0,
  userId: "",
};

export const useTelemetryStore = create<TelemetryStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setBrowser: value => set({ browser: value }),

      setIdleSignOutCount: value =>
        set(state => ({
          idleSignOutCount:
            typeof value === "function" ? value(state.idleSignOutCount) : value,
        })),

      setIdleTime: value =>
        set(state => ({
          idleTime: typeof value === "function" ? value(state.idleTime) : value,
        })),

      setIdleWarningCount: value =>
        set(state => ({
          idleWarningCount:
            typeof value === "function" ? value(state.idleWarningCount) : value,
        })),

      setLanguage: value => set({ language: value }),

      setOs: value => set({ os: value }),

      setScreenOrientation: value => set({ screenOrientation: value }),

      setScreenResolution: value => set({ screenResolution: value }),

      setSignInAttempts: value =>
        set(state => ({
          signInAttempts:
            typeof value === "function" ? value(state.signInAttempts) : value,
        })),

      setTotalTimeSpent: value =>
        set(state => ({
          totalTimeSpent:
            typeof value === "function" ? value(state.totalTimeSpent) : value,
        })),

      setTimestamp: value => set({ timestamp: value }),

      setUserId: value => set({ userId: value }),

      checkAndSendTelemetry: submitTelemetryFn => {
        // import.meta.env.DEV &&
        console.log(
          `[Telemetry] - Will submit every ${
            SUBMIT_INTERVAL / 1000 / 60 / 60
          } hour(s)`
        );

        // Get the telemetry store
        if (get().userId && isTelemetryDue(get().timestamp as string)) {
          // import.meta.env.DEV &&
          console.log(
            `[Telemetry] - Submitting this data at [${new Date().toISOString()}]:`,
            get()
          );
          submitTelemetryFn();
          // Handle resetting in submitTelemetryFn
        } else {
          // import.meta.env.DEV &&
          console.log(
            "[Telemetry] - Not due or no userId found at",
            new Date().toISOString()
          );
        }
      },

      startTelemetryInterval: submitTelemetryFn => {
        // import.meta.env.DEV &&
        console.log(
          `[Telemetry] - Will check every ${
            CHECK_INTERVAL / 1000 / 60 / 60
          } hour(s)`
        );

        const isTelemetryEnabled = JSON.parse(
          import.meta.env.VITE_ENABLE_TELEMETRY
        );
        if (!isTelemetryEnabled) {
          get().resetTelemetry();
          // import.meta.env.DEV &&
          console.log("[Telemetry] - is disabled");
          return undefined;
        } else {
          get().checkAndSendTelemetry(submitTelemetryFn);
          // Set an interval to check the telemetry timestamp every hour
          return setInterval(() => {
            get().checkAndSendTelemetry(submitTelemetryFn);
          }, CHECK_INTERVAL);
        }
      },

      resetTelemetry: () => set(initialState),
    }),
    {
      name: `${LARUCHE_IDENTIFIER}-telemetry-store`,
      version: 1.0,
      migrate(persistedState, version) {
        // if (version < x.x) {
        // Just a simple reset
        persistedState = initialState;
        return persistedState;
        // }
      },
    }
  )
);
